/**
 * The broker topic, `brokerpipe-updates` — the contract between the writers and the consumers (ADR-047 §3).
 *
 * WHERE THE VOLUME COMES FROM. The load topic page reads its evidence end to end, from what its consumer stored. The
 * broker topic has no consumer yet, so the volume here is the writers' own count of what `publishBrokerUpdate`
 * accepted, per action, from each writer's activity collection. A writer states one type per source, so its type is
 * known. When the search consumer lands, its side (lag, refusals) belongs beside this, as on the load topic.
 *
 * Subscription backlog and dead-letter depth live in the Pub/Sub monitoring API, which this service does not read; they
 * are not shown rather than shown as zero.
 */

import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Card from '@mui/joy/Card'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import * as React from 'react'
import { brokerPipelineApi } from 'api/brokerPipeline'
import { ErrorState, Loading, Stat, StatRow, WindowPicker, formatNumber, useLoadPipelineData } from '../load-pipeline/components/shared'
import { writerTitle } from './components/shared'

/** What the topic carries (ADR-047 §3.1). Static: it is the contract, and both sets are closed. */
const TYPES: Array<{ type: string; means: string; actions: string; sources: string }> = [
    { type: 'REGISTER', means: 'a public register states it; the only type that creates a brokerage', actions: 'STATE · CONFIRM', sources: 'FMCSA' },
    { type: 'RATED', means: 'a credit bureau’s opinion at a time', actions: 'STATE · CONFIRM', sources: 'RTS · TRIUMPH' },
    { type: 'OBSERVED', means: 'a channel showed it', actions: 'STATE', sources: 'RATECONF · EMAIL_SENDER · DAT_DIRECTORY · DAT_POSTER · WEB' },
    { type: 'ASSERTED', means: 'the brokerage says so about itself', actions: 'STATE · REMOVE', sources: 'PORTAL' },
    { type: 'CORRECTED', means: 'a Numeo operator fixed it, with a reason', actions: 'STATE · REMOVE', sources: 'OPERATOR' },
    { type: 'REPORTED', means: 'a user says a value is wrong', actions: 'STATE', sources: 'USER_REPORT' },
]

const mono = { fontFamily: 'monospace' } as const

export default function BrokerPipelineTopicPage() {
    const [hours, setHours] = React.useState(24)
    const { data, error, loading } = useLoadPipelineData(() => brokerPipelineApi.getTopic(hours), [hours])

    const total = (data?.rows ?? []).reduce((sum, r) => sum + r.count, 0)
    const byAction = (action: string) => (data?.rows ?? []).filter((r) => r.action === action).reduce((sum, r) => sum + r.count, 0)

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography level="h2">T-Broker Updates</Typography>
                <WindowPicker hours={hours} onChange={setHours} maxHours={168} />
            </Box>
            <Typography level="body-sm" textColor="text.tertiary" sx={{ mb: 3 }}>
                <code>brokerpipe-updates</code>: one topic for every writer, ordered by MC, kept 7 days (ADR-047 §3.2). The volume below is what each writer
                counted as published; no consumer reads the topic yet, so there is no end-to-end evidence to show beside it.
            </Typography>

            {error ? <ErrorState error={error} /> : null}
            {loading && !data ? <Loading /> : null}

            {data ? (
                <>
                    <StatRow>
                        <Stat label="Published" value={formatNumber(total)} hint="every action, every built writer" />
                        <Stat label="STATE" value={formatNumber(byAction('STATE'))} hint="what a source showed" />
                        <Stat label="CONFIRM" value={formatNumber(byAction('CONFIRM'))} hint="unchanged since the source last stated it" />
                        <Stat label="REMOVE" value={formatNumber(byAction('REMOVE'))} hint="a withdrawn person or contact point" />
                    </StatRow>

                    {data.unavailable.length ? (
                        <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
                            Could not read the counters of {data.unavailable.map(writerTitle).join(', ')}: the volume is missing their share.
                        </Alert>
                    ) : null}

                    {data.rows.length === 0 ? (
                        <Alert color="neutral" variant="soft" sx={{ mb: 3 }}>
                            Nothing was published in this window. Until the FMCSA seed runs, the topic is empty.
                        </Alert>
                    ) : (
                        <Sheet variant="outlined" sx={{ borderRadius: 'sm', mb: 3, overflow: 'auto' }}>
                            <Table size="sm" stripe="odd">
                                <thead>
                                    <tr>
                                        <th>Type</th>
                                        <th>Action</th>
                                        <th>Writer</th>
                                        <th style={{ textAlign: 'right' }}>Updates</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.rows.map((r) => (
                                        <tr key={`${r.writer}|${r.action}`}>
                                            <td style={mono}>{r.type}</td>
                                            <td style={mono}>{r.action}</td>
                                            <td>{writerTitle(r.writer)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatNumber(r.count)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Sheet>
                    )}

                    <Alert color="neutral" variant="soft" sx={{ mb: 3 }}>
                        <Typography level="body-sm">
                            <strong>Subscription backlog and dead-letter depth are not shown.</strong> They live in the Pub/Sub monitoring API, which this
                            service does not read; a stalled subscription and an idle one both read zero.
                        </Typography>
                    </Alert>
                </>
            ) : null}

            <Card>
                <Typography level="title-md">What the topic carries</Typography>
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 1 }}>
                    Every update has a type, what kind of statement it is, and an action. Both sets are closed; each source has exactly one type, and only
                    <code> REGISTER</code> creates a brokerage.
                </Typography>
                <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                    <Table size="sm">
                        <thead>
                            <tr>
                                <th style={{ width: 120 }}>Type</th>
                                <th>Means</th>
                                <th style={{ width: 160 }}>Actions</th>
                                <th style={{ width: 280 }}>Sources</th>
                            </tr>
                        </thead>
                        <tbody>
                            {TYPES.map((t) => (
                                <tr key={t.type}>
                                    <td style={mono}>{t.type}</td>
                                    <td>{t.means}</td>
                                    <td style={mono}>{t.actions}</td>
                                    <td style={mono}>{t.sources}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Sheet>
            </Card>
        </Box>
    )
}
